import { FC, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Typography, Stack, Divider, MenuItem, ListItemIcon, ListItemText, MenuList, Link, Menu } from "@mui/material";
import { keyframes } from "@emotion/react";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import ApiRoundedIcon from "@mui/icons-material/ApiRounded";
import WebhookRoundedIcon from "@mui/icons-material/WebhookRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import { InstanceAvatar } from "../InstanceAvatar";
import { InstancesList } from "./Flyouts/InstancesList";
import { GlobalDomainsMenu } from "../../../GlobalDomainsMenu";
import { useGetInstanceQuery } from "../../../../services/accounts";
import { useRefreshCacheMutation } from "../../../../services/cloudFunctions";
import { useDomain } from "../../../../hooks/use-domain";
import { notify } from "../../../../store/notifications";
import instanceZUID from "../../../../../utility/instanceZUID";
import { actions } from "../../../../store/ui";
import { CascadingMenuItem } from "../../../CascadingMenuItem";
import { useTranslation } from "react-i18next";
export const rotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;
export type View = "normal" | "instances" | "domains";
interface DropdownMenuProps {
  anchorEl: HTMLElement;
  onClose: () => void;
}
export const DropdownMenu: FC<DropdownMenuProps> = ({
  anchorEl,
  onClose
}) => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const [isInstanceZuidCopied, setIsInstanceZuidCopied] = useState(false);
  const {
    data: instance
  } = useGetInstanceQuery();
  const [refreshCache, {
    data,
    isSuccess: isCacheRefreshed,
    isLoading: isCacheRefreshing,
    isError: isCacheRefreshFailed
  }] = useRefreshCacheMutation();
  const domain = useDomain();
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isInstanceZuidCopied) {
      timeoutId = setTimeout(() => setIsInstanceZuidCopied(false), 1500);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isInstanceZuidCopied]);
  useEffect(() => {
    if (isCacheRefreshFailed) {
      dispatch(notify({
        message: t("shell.failedRefreshCdnCache"),
        kind: "error"
      }));
    }
  }, [isCacheRefreshFailed]);
  const handleOpenUrl = (url: string) => {
    onClose();
    window.open(url, "_blank", "noopener");
  };
  const handleCopyInstanceZUID = async () => {
    try {
      await navigator?.clipboard.writeText(instanceZUID);
      setIsInstanceZuidCopied(true);
    } catch (error) {
      dispatch(notify({
        message: t("shell.failedCopyInstanceZUID"),
        kind: "error"
      }));
    }
  };
  return <>
      <Menu open data-cy="InstanceMenu" anchorEl={anchorEl} onClose={onClose} PaperProps={{
      sx: {
        pt: 0,
        mt: 1.5,
        width: 340,
        borderRadius: "8px",
        elevation: 8,
        overflow: "hidden"
      }
    }} MenuListProps={{
      sx: {
        p: 0,
        height: 591
      }
    }} data-sentry-element="Menu" data-sentry-source-file="DropdownMenu.tsx">
        <Stack direction="row" gap={1.5} p={2} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="DropdownMenu.tsx">
          <InstanceAvatar onFaviconModalOpen={() => onClose()} data-sentry-element="InstanceAvatar" data-sentry-source-file="DropdownMenu.tsx" />
          <Stack data-sentry-element="Stack" data-sentry-source-file="DropdownMenu.tsx">
            <Typography variant="body2" fontWeight={600} data-sentry-element="Typography" data-sentry-source-file="DropdownMenu.tsx">
              {instance?.name}
            </Typography>
            {Boolean(domain) && <Link variant="body2" fontWeight={600} color="info.dark" href={domain} target="_blank" rel="noopener">
                {domain.replace(/http:\/\/|https:\/\//gm, "")}
              </Link>}
          </Stack>
        </Stack>
        <Divider data-sentry-element="Divider" data-sentry-source-file="DropdownMenu.tsx" />
        <MenuList data-sentry-element="MenuList" data-sentry-source-file="DropdownMenu.tsx">
          <InstancesList data-sentry-element="InstancesList" data-sentry-source-file="DropdownMenu.tsx" />
          <Divider data-sentry-element="Divider" data-sentry-source-file="DropdownMenu.tsx" />
          <MenuItem onClick={() => {
          onClose();
          dispatch(actions.toggleUpdateFaviconModal(true));
        }} data-cy="Favicon" data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              <ImageRoundedIcon data-sentry-element="ImageRoundedIcon" data-sentry-source-file="DropdownMenu.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("shell.updateFavicon")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleOpenUrl(`${CONFIG.URL_PREVIEW_PROTOCOL}${instance.randomHashID}${CONFIG.URL_PREVIEW}`)} data-cy="WebEnginePreviewLink" data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              <RemoveRedEyeRoundedIcon data-sentry-element="RemoveRedEyeRoundedIcon" data-sentry-source-file="DropdownMenu.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("shell.viewWebEnginePreview")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleCopyInstanceZUID} data-cy="CopyInstanceZUID" data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              {isInstanceZuidCopied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("shell.copyInstanceZUID")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => refreshCache()} data-cy="RefreshCache" data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              {isCacheRefreshed ? <CheckRoundedIcon /> : <RefreshRoundedIcon sx={{
              animation: isCacheRefreshing ? `${rotateAnimation} 1s infinite ease` : "none"
            }} />}
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("shell.refreshCdnCache")}</ListItemText>
          </MenuItem>
          <Divider data-sentry-element="Divider" data-sentry-source-file="DropdownMenu.tsx" />
          <MenuItem onClick={() => handleOpenUrl(`https://www.zesty.io/instances/${instanceZUID}/users`)} data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              <PeopleRoundedIcon data-sentry-element="PeopleRoundedIcon" data-sentry-source-file="DropdownMenu.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("common.users")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleOpenUrl(`https://www.zesty.io/instances/${instanceZUID}/teams`)} data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              <GroupsRoundedIcon data-sentry-element="GroupsRoundedIcon" data-sentry-source-file="DropdownMenu.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("common.teams")}</ListItemText>
          </MenuItem>
          <CascadingMenuItem data-cy="DomainSwitcherMenuItem" MenuItemComponent={<>
                <ListItemIcon>
                  <LanguageRoundedIcon />
                </ListItemIcon>
                <ListItemText>{t("common.domains")}</ListItemText>
                <ArrowForwardIosRoundedIcon color="action" fontSize="small" />
              </>} PopperProps={{
          popperOptions: {
            modifiers: [{
              name: "offset",
              options: {
                offset: [-16, -8]
              }
            }, {
              name: "flip",
              enabled: false
            }]
          }
        }} PaperProps={{
          sx: {
            borderRadius: "8px",
            // width: 340,
            mb: 2
          }
        }} data-sentry-element="CascadingMenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <GlobalDomainsMenu withBackButton={false} data-sentry-element="GlobalDomainsMenu" data-sentry-source-file="DropdownMenu.tsx" />
          </CascadingMenuItem>
          <MenuItem onClick={() => handleOpenUrl(`https://www.zesty.io/instances/${instanceZUID}/usage`)} data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              <BarChartRoundedIcon data-sentry-element="BarChartRoundedIcon" data-sentry-source-file="DropdownMenu.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("common.usage")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleOpenUrl(`https://www.zesty.io/instances/${instanceZUID}/locales`)} data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              <TranslateRoundedIcon data-sentry-element="TranslateRoundedIcon" data-sentry-source-file="DropdownMenu.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("common.locales")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleOpenUrl(`https://www.zesty.io/instances/${instanceZUID}/apis`)} data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              <ApiRoundedIcon data-sentry-element="ApiRoundedIcon" data-sentry-source-file="DropdownMenu.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("common.apis")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleOpenUrl(`https://www.zesty.io/instances/${instanceZUID}/webhooks`)} data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              <WebhookRoundedIcon data-sentry-element="WebhookRoundedIcon" data-sentry-source-file="DropdownMenu.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("common.webhooks")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleOpenUrl(`https://www.zesty.io/instances/${instanceZUID}/support`)} data-cy="GetHelp" data-sentry-element="MenuItem" data-sentry-source-file="DropdownMenu.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="DropdownMenu.tsx">
              <SupportAgentRoundedIcon data-sentry-element="SupportAgentRoundedIcon" data-sentry-source-file="DropdownMenu.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="DropdownMenu.tsx">{t("shell.contactSupport")}</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>
    </>;
};